/**
 * The reviewer's credential, held in memory for the life of the tab.
 *
 * `docs/security.md` forbids `ADMIN_API_KEY` reaching browser code, so the desk
 * cannot ship with a key: the operator supplies one and this holds it until the
 * page is closed. Nothing is written to `localStorage`, `sessionStorage`, a
 * cookie, or the URL. Any script on the page can read the first two, so a key
 * parked in either survives the tab and leaves with the first cross-site
 * scripting defect — current OWASP guidance is memory for a short-lived
 * credential, and memory means memory.
 *
 * The cost is deliberate and is the point: a reload signs the operator out.
 *
 * It is sent as `x-api-key`, a header a script sets rather than a credential the
 * browser attaches, so it is not ambient and carries no cross-site request
 * forgery exposure. That is what keeps CSRF out of scope here, and it stops
 * being true the moment anything moves to a cookie.
 */
import { createContext, createElement, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Credential = {
  /** The key to present, or null when nobody has signed in. */
  key: string | null;
  /**
   * Changes whenever the key does.
   *
   * Query caches are keyed by whatever they are given, so the key itself must
   * never be one: it would sit in devtools and in anything that walks cached
   * state. This is the non-secret stand-in that makes a cached answer for one
   * credential unreachable under another.
   */
  epoch: number;
  setKey: (value: string) => void;
  clear: () => void;
};

const CredentialContext = createContext<Credential | null>(null);

export function CredentialProvider({ children }: { children: ReactNode }) {
  const [key, setStoredKey] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);

  const setKey = useCallback((value: string) => {
    setStoredKey(value);
    setEpoch((previous) => previous + 1);
  }, []);

  const clear = useCallback(() => {
    setStoredKey(null);
    setEpoch((previous) => previous + 1);
  }, []);

  const value = useMemo(() => ({ key, epoch, setKey, clear }), [key, epoch, setKey, clear]);
  return createElement(CredentialContext.Provider, { value }, children);
}

export function useCredential(): Credential {
  const held = useContext(CredentialContext);
  if (held === null) {
    // Thrown rather than defaulted. A silent "nobody signed in" renders the desk
    // exactly as a wrong key would, so a missing provider would be debugged as
    // a credential problem.
    throw new Error('useCredential must be used inside a CredentialProvider');
  }
  return held;
}

/**
 * Returns the headers a request should carry for the held credential.
 *
 * The one place that knows the credential is a header at all. A later adapter
 * putting a session cookie in its place changes this function and no caller.
 */
export function credentialHeaders(key: string | null): Record<string, string> {
  return key ? { 'x-api-key': key } : {};
}
